import { useRef, useState, useEffect, useCallback } from 'react';
import {
    Eraser,
    PenTool,
    Type,
    CheckCircle2,
    AlertCircle,
    Info,
    Undo2,
} from 'lucide-react';

// ==================== Types ====================
type SignatureMode = 'draw' | 'type';

interface SignatureFieldProps {
    onSignatureChange: (signatureData: string | null, isValid: boolean) => void;
    width?: number;
    height?: number;
    strokeColor?: string;
    strokeWidth?: number;
    minStrokeLength?: number;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
}

// ==================== Component ====================
export const SignatureField: React.FC<SignatureFieldProps> = ({
    onSignatureChange,
    width = 600,
    height = 150,
    strokeColor = '#1e3a5f',
    strokeWidth = 2,
    minStrokeLength = 50,
    placeholder = 'Assine aqui',
    className = '',
    disabled = false,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [mode, setMode] = useState<SignatureMode>('draw');
    const [typedName, setTypedName] = useState('');
    const [totalStrokeLength, setTotalStrokeLength] = useState(0);
    const [lastPoint, setLastPoint] = useState<{ x: number; y: number } | null>(null);
    const [showTip, setShowTip] = useState(true);

    // ==================== Canvas Setup ====================
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || mode !== 'draw') return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Setup canvas styling
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = strokeWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Clear and set background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw baseline
        ctx.strokeStyle = '#e5e7eb';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(20, height - 30);
        ctx.lineTo(width - 20, height - 30);
        ctx.stroke();

        // Reset stroke style
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = strokeWidth;
    }, [mode, strokeColor, strokeWidth, width, height]);

    // ==================== Validation ====================
    const validateSignature = useCallback(
        (length: number) => {
            const valid = length >= minStrokeLength;
            setIsValid(valid);
            return valid;
        },
        [minStrokeLength]
    );

    // ==================== Get Coordinates ====================
    const getCoordinates = (
        e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
    ) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        if ('touches' in e && e.touches.length > 0) {
            return {
                x: (e.touches[0]!.clientX - rect.left) * scaleX,
                y: (e.touches[0]!.clientY - rect.top) * scaleY,
            };
        } else if ('clientX' in e) {
            return {
                x: (e.clientX - rect.left) * scaleX,
                y: (e.clientY - rect.top) * scaleY,
            };
        }
        return { x: 0, y: 0 };
    };

    // ==================== Drawing Handlers ====================
    const startDrawing = (
        e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
    ) => {
        if (disabled) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        e.preventDefault();
        setIsDrawing(true);
        setHasSignature(true);
        setShowTip(false);

        const coords = getCoordinates(e);
        setLastPoint(coords);

        ctx.beginPath();
        ctx.moveTo(coords.x, coords.y);
    };

    const draw = (
        e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
    ) => {
        if (!isDrawing || disabled) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        e.preventDefault();
        const coords = getCoordinates(e);

        // Calculate stroke length
        if (lastPoint) {
            const dx = coords.x - lastPoint.x;
            const dy = coords.y - lastPoint.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const newTotalLength = totalStrokeLength + distance;
            setTotalStrokeLength(newTotalLength);
            validateSignature(newTotalLength);
        }

        setLastPoint(coords);

        ctx.lineTo(coords.x, coords.y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        if (!isDrawing) return;
        setIsDrawing(false);
        setLastPoint(null);

        // Notify parent of signature change
        if (hasSignature) {
            const canvas = canvasRef.current;
            if (canvas) {
                const signatureData = canvas.toDataURL('image/png');
                onSignatureChange(signatureData, isValid);
            }
        }
    };

    // ==================== Clear Signature ====================
    const clearSignature = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Clear canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Redraw baseline
        ctx.strokeStyle = '#e5e7eb';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(20, height - 30);
        ctx.lineTo(width - 20, height - 30);
        ctx.stroke();

        // Reset stroke style
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = strokeWidth;

        setHasSignature(false);
        setIsValid(false);
        setTotalStrokeLength(0);
        setShowTip(true);
        onSignatureChange(null, false);
    };

    // ==================== Typed Signature ====================
    const handleTypedNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.value;
        setTypedName(name);
        setHasSignature(name.length > 0);

        if (name.length >= 3) {
            setIsValid(true);
            // Generate typed signature as image
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, width, height);
                ctx.font = 'italic 48px "Brush Script MT", cursive, serif';
                ctx.fillStyle = strokeColor;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(name, width / 2, height / 2);
                onSignatureChange(canvas.toDataURL('image/png'), true);
            }
        } else {
            setIsValid(false);
            onSignatureChange(null, false);
        }
    };

    // ==================== Mode Switch ====================
    const switchMode = (newMode: SignatureMode) => {
        if (newMode === mode) return;
        clearSignature();
        setTypedName('');
        setMode(newMode);
    };

    // ==================== Render ====================
    return (
        <div className={`${className}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <PenTool className="w-5 h-5 text-sky-400" />
                    <h3 className="font-semibold text-white">Sua Assinatura</h3>
                </div>

                {/* Mode Toggle */}
                <div className="flex bg-white/5 rounded-lg p-1">
                    <button
                        onClick={() => switchMode('draw')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${mode === 'draw'
                            ? 'bg-sky-500 text-white'
                            : 'text-white/60 hover:text-white'
                            }`}
                        disabled={disabled}
                    >
                        <PenTool className="w-4 h-4" />
                        Desenhar
                    </button>
                    <button
                        onClick={() => switchMode('type')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${mode === 'type'
                            ? 'bg-sky-500 text-white'
                            : 'text-white/60 hover:text-white'
                            }`}
                        disabled={disabled}
                    >
                        <Type className="w-4 h-4" />
                        Digitar
                    </button>
                </div>
            </div>

            {/* Instructions */}
            <div className="flex items-start gap-2 mb-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-300">
                    {mode === 'draw'
                        ? 'Desenhe sua assinatura usando o mouse ou o dedo (em dispositivos touch). A assinatura deve ter pelo menos alguns traços.'
                        : 'Digite seu nome completo. Ele será renderizado como uma assinatura estilizada.'}
                </p>
            </div>

            {/* Signature Area */}
            <div className="relative">
                {mode === 'draw' ? (
                    <>
                        <canvas
                            ref={canvasRef}
                            width={width}
                            height={height}
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={stopDrawing}
                            className={`w-full bg-white rounded-xl cursor-crosshair touch-none ${disabled ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            style={{ maxWidth: '100%', height: 'auto' }}
                        />

                        {/* Placeholder */}
                        {showTip && !hasSignature && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <p className="text-gray-400 text-lg">{placeholder}</p>
                            </div>
                        )}

                        {/* Clear Button */}
                        {hasSignature && (
                            <button
                                onClick={clearSignature}
                                className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 text-sm transition-colors"
                                title="Limpar assinatura"
                                disabled={disabled}
                            >
                                <Eraser className="w-4 h-4" />
                                Limpar
                            </button>
                        )}
                    </>
                ) : (
                    <div className="bg-white rounded-xl p-6">
                        <input
                            type="text"
                            value={typedName}
                            onChange={handleTypedNameChange}
                            placeholder="Digite seu nome completo"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20"
                            disabled={disabled}
                        />
                        {typedName && (
                            <div className="mt-4 p-4 bg-gray-50 rounded-lg border-b-2 border-gray-300">
                                <p
                                    className="text-4xl text-center"
                                    style={{
                                        fontFamily: '"Brush Script MT", cursive, serif',
                                        fontStyle: 'italic',
                                        color: strokeColor,
                                    }}
                                >
                                    {typedName}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Validation Feedback */}
            <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-2">
                    {hasSignature ? (
                        isValid ? (
                            <>
                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                <span className="text-sm text-emerald-400">Assinatura válida</span>
                            </>
                        ) : (
                            <>
                                <AlertCircle className="w-4 h-4 text-amber-400" />
                                <span className="text-sm text-amber-400">
                                    {mode === 'draw'
                                        ? 'Continue desenhando para validar'
                                        : 'Digite pelo menos 3 caracteres'}
                                </span>
                            </>
                        )
                    ) : (
                        <span className="text-sm text-white/40">Aguardando assinatura</span>
                    )}
                </div>

                {mode === 'draw' && hasSignature && (
                    <button
                        onClick={clearSignature}
                        className="flex items-center gap-1 text-sm text-white/50 hover:text-white transition-colors"
                        disabled={disabled}
                    >
                        <Undo2 className="w-4 h-4" />
                        Recomeçar
                    </button>
                )}
            </div>
        </div>
    );
};
